import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, ArrowRight, Clock, Zap, CheckCircle } from 'lucide-react';

const ExitIntentPopup = ({ 
  onClose,
  redirectUrl = 'https://app.meumei.net/register'
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const showPopup = useCallback(() => {
    // Verifica se já mostrou o popup nesta sessão
    if (sessionStorage.getItem('exitPopupShown')) {
      return;
    }
    setIsVisible(true);
    sessionStorage.setItem('exitPopupShown', 'true');
  }, []);

  useEffect(() => {
    // Delay de 3 segundos antes de ativar o detector
    const activationTimer = setTimeout(() => {
      
      // Detecta movimento do mouse saindo da página (desktop) - apenas na parte superior
      const handleMouseLeave = (e) => {
        // Só dispara se o mouse sair pelo topo da página (barra de endereço)
        if (e.clientY <= 5 && e.relatedTarget === null) {
          showPopup();
        }
      };

      // Adiciona listener apenas no documento
      document.addEventListener('mouseout', handleMouseLeave);

      return () => {
        document.removeEventListener('mouseout', handleMouseLeave);
      };
    }, 3000); // Espera 3 segundos antes de ativar

    return () => clearTimeout(activationTimer);
  }, [showPopup]);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Redireciona para o registro
    window.location.href = redirectUrl;
  };

  const beneficios = [
    'Emita notas fiscais ilimitadas',
    'Controle financeiro completo',
    'DAS gerado automaticamente',
    'Declaração anual facilitada',
  ];

  // Não renderiza nada se não estiver visível
  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-3xl overflow-hidden max-w-lg w-full shadow-2xl"
          >
            {/* Botão fechar */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center z-10 transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            {/* Header com gradiente */}
            <div 
              className="relative pt-12 pb-8 px-8 text-center"
              style={{
                background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              }}
            >
              {/* Ícone animado */}
              <motion.div
                animate={{ 
                  rotate: [0, -10, 10, -10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  duration: 0.5,
                  repeat: Infinity,
                  repeatDelay: 2
                }}
                className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <Gift className="w-10 h-10 text-white" />
              </motion.div>

              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Espere! 🛑
              </h2>
              <p className="text-white/90 text-lg">
                Você está perdendo uma oferta especial!
              </p>
            </div>

            {/* Conteúdo */}
            <div className="px-8 py-6">
              {/* Oferta */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-6 mb-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-emerald-600" />
                  <span className="text-emerald-700 font-semibold">Oferta por tempo limitado</span>
                </div>
                <p className="text-3xl font-bold text-emerald-600 mb-1">
                  7 DIAS GRÁTIS
                </p>
                <p className="text-emerald-700">
                  + Acesso completo a todas as funcionalidades
                </p>
              </div>

              {/* Benefícios */}
              <div className="space-y-3 mb-6">
                {beneficios.map((beneficio, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-gray-700">{beneficio}</span>
                  </motion.div>
                ))}
              </div>

              {/* Botão */}
              <motion.button
                onClick={handleSubmit}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-shadow"
              >
                <Zap className="w-5 h-5" />
                QUERO MEUS 7 DIAS GRÁTIS
                <ArrowRight className="w-5 h-5" />
              </motion.button>

              {/* Link secundário */}
              <button
                onClick={handleClose}
                className="w-full mt-4 text-gray-500 text-sm hover:text-gray-700 transition-colors"
              >
                Não, obrigado. Prefiro pagar o preço cheio depois.
              </button>

              {/* Trust badges */}
              <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-center gap-2 text-xs text-gray-500">
                <span>🔒 Seus dados estão seguros</span>
                <span>•</span>
                <span>Cancele quando quiser</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;